/* eslint-disable react/jsx-props-no-spreading */
import { jobDetailType } from "src/components/common/MainContent/JobResults/common";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LightModeIcon from "@mui/icons-material/LightMode";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EligibilityMeter from "src/components/common/EligibilityMeter/EligibilityMeter";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import { Skeleton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation, withTranslation } from "react-i18next";
import { ReactElement } from "react";
import SyncIcon from "@mui/icons-material/Sync";
import NightlightIcon from "@mui/icons-material/Nightlight";
import Brightness6Icon from "@mui/icons-material/Brightness6";
import JobCardChip, { JobCardChipProps } from "../chip/JobCardChip";
import styles from "./JobCardContent.module.scss";
import JobDisclaimerToolTip from "../../../JobResults/JobDisclaimerToolTip";
import "./override.scss";
import EmailAndFavoriteIcons from "../email-and-favorite-icons/EmailAndFavoriteIcons";

export
  const renderShiftDetails = (job: jobDetailType) => {
    const shift = job?.shift?.toLowerCase();

    if (shift) {
      const hoursAmount = shift.split("hr")?.[0];
      let type: string | undefined = shift.split(" ")?.[1];
      let icon: ReactElement | undefined;

      switch (type) {
        case "days":
          type = "Days";
          icon = <LightModeIcon />;
          break;
        case "evenings":
          type = "Mid";
          icon = <Brightness6Icon />;
          break;
        case "nights":
          type = "Nights";
          icon = <NightlightIcon />;
          break;
        case "rotating":
          type = "Rotating d/e";
          icon = <SyncIcon />;
          break;
        default:
          type = undefined;
          icon = undefined;
          break;
      }

      if (hoursAmount && type && icon) {
        return (
          <>
            {icon} {hoursAmount}Hr {type}
          </>
        )
      }
    }

    return "";
  }

export interface JobCardContentProps {
  job?: jobDetailType;
  chips?: JobCardChipProps[];
  secondaryInfo?: "quick-apply" | "views" | "similarJobs" | "peopleViewed";
  isHotJob?: boolean;
  compact?: boolean;
  className?: string;
  onClick?: () => void;
  campaignCode: string;
  showIconsInPrimaryColor?: boolean;
  showContactAction?: boolean;
  showFavoriteAction?: boolean;
  sourceType: string;
  recommended?: boolean;
  isRecommendedJobsOnResult?: boolean;
  similarOrViewed?: string;
  viewMyJobs?: boolean;
  onEmailClick: () => void;
  modifyJob?: (partialJob: Partial<jobDetailType>) => void;
}


const JobCardContent = ({
  job,
  chips,
  secondaryInfo,
  compact,
  isHotJob,
  className,
  onClick,
  campaignCode,
  showIconsInPrimaryColor,
  showContactAction,
  showFavoriteAction,
  sourceType,
  recommended,
  isRecommendedJobsOnResult,
  similarOrViewed,
  viewMyJobs,
  onEmailClick,
  modifyJob,
}: JobCardContentProps) => {

  const { t } = useTranslation();
  const jobViews = secondaryInfo === "views" && (job?.views ?? 0) >= 10;
  const quickApplyAdvertisement = campaignCode === "Quick Apply Eligible"
  const eligibilityPercentage = job?.quickApplyEligibility?.eligibilityPercentage;
  const eligibilityMeter = !eligibilityPercentage || eligibilityPercentage <= 20;
  const similarAndPeopleViewed = secondaryInfo === "similarJobs" || secondaryInfo === "peopleViewed";
  const showEligibilityMeter = secondaryInfo === "quick-apply" || secondaryInfo === "similarJobs" || secondaryInfo === "peopleViewed";
  const noJobViews = secondaryInfo === "views" && (job?.views ?? 0) <= 10;


  if (job == null) {
    return (
      <div className={clsx(styles.main, className)}>
        {Array.from({ length: 5 }).map((_, index) => {
          const isLast = index === 4;
          const marginBottomAmount = compact ? "5px" : "10px";
          const marginBottom = isLast ? 0 : marginBottomAmount;

          return <Skeleton
            key={uuidv4()}
            variant="rectangular"
            className={styles.skeleton}
            height={compact ? "10px" : "20px"}
            width="100%"
            style={{ marginBottom, borderRadius: "5px" }}
          />
        })}
      </div>
    );
  }

const getCityStateClasses = () => {
  if (isHotJob) return styles.cityStateHotJobs;
  if (jobViews) return clsx(styles.cityState, styles.cityStateJobViews);
  if (noJobViews) return clsx(styles.cityState, styles.cityStateNoJobViews);
  if (showEligibilityMeter && eligibilityMeter){
    if(quickApplyAdvertisement){
      return clsx(styles.cityState, styles.quickApplyNoEligiblityMeter);
    }
    return clsx(styles.cityState, styles.cityStateNoEligiblityMeter);
  }
  if (similarAndPeopleViewed) return clsx(styles.cityState, styles.cityStateSimilarPeopleViewed);
  if (quickApplyAdvertisement) return clsx(styles.cityState, styles.cityStateQuickApplyEligible);
  return styles.cityState;
};

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={() => onClick?.()}
      onClick={() => onClick?.()}
      data-testid={`${campaignCode}`}
      className={clsx(styles.main, compact && styles.compact, compact && "compact", className)}
    >
      <div className={clsx(styles.top, isHotJob || recommended ? styles.topMarginBottom : "")}>
        <div>
          {chips != null && chips.length > 0 && (
            <div className={styles.chips}>
              {chips.map((chipProps: JobCardChipProps) => (
                <JobCardChip
                  key={uuidv4()}
                  {...chipProps}
                  size="compact"
                  className={clsx(styles.chip, chipProps.className)}
                />
              ))}
            </div>
          )}
        </div>
        <EmailAndFavoriteIcons
          showContactAction={showContactAction}
          showFavoriteAction={showFavoriteAction}
          recommended={recommended}
          isRecommendedJobsOnResult={isRecommendedJobsOnResult}
          sourceType={sourceType}
          campaignCode={campaignCode}
          job={job}
          viewMyJobs={viewMyJobs}
          similarOrViewed={similarOrViewed}
          onEmailClick={onEmailClick}
          showIconsInPrimaryColor={showIconsInPrimaryColor}
          modifyJob={modifyJob}
        />
      </div>
      <div className={styles.locationAndSecondaryInfo}>
       <div>
        <div className={getCityStateClasses()}>
          <span className={clsx(styles.city)}> 
            <LocationOnIcon />
            {job.city}
          </span>
            <span>  
            , {job.state}
            </span> 
        </div>
          <div className={styles.facilityName}>
            {job.facilityName}
          </div>
        </div>
        {secondaryInfo != null && (
          <div className={styles.secondaryInfo}>
            {showEligibilityMeter && (
              <EligibilityMeter
                showQuickApplyText
                eligibilityPercentage={job?.quickApplyEligibility?.eligibilityPercentage}
              />
            )}

            {jobViews && (
              <div className={styles.viewsContainer}>
                <VisibilityIcon />
                {(job?.views ?? 0).toLocaleString()} {" "}
                {t("dashboard.jobsViews.views")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.salary}>
        {job.salaryMax != null ? `$${job.salaryMax.toLocaleString()}` : "Unknown"}/Weekly
        <JobDisclaimerToolTip
          translate={t}
          isJobDetail={false}
          salaryHourly={job.salaryHourly}
          salaryWeekly={job.salaryExpenseReimbursementWeekly}
          isSalary
        />

      </div>

      <div className={styles.professionAndSpecialty}>
        {job.professionDescription} / {job.specialtyDescription}
      </div>

      <div className={styles.extraInfo}>
        <div className={styles.shift}>
          {renderShiftDetails(job)}
        </div>

        <div className={styles.hoursPerWeek}>
          <AccessTimeIcon />
          {job.hoursPerWeek}Hrs/Wk
        </div>

        <div className={styles.duration}>
          <CalendarTodayIcon />
          {job.duration} Wk
        </div>
      </div>
    </div>
  );
}

JobCardContent.defaultProps = {
  job: undefined,
  chips: [],
  secondaryInfo: "",
  compact: false,
  isHotJob: false,
  className: "",
  onClick: undefined,
  showIconsInPrimaryColor: false,
  showContactAction: false,
  showFavoriteAction: false,
  isRecommendedJobsOnResult: false,
  recommended: false,
  similarOrViewed: "",
  viewMyJobs: false,
  modifyJob:undefined
}

export default withTranslation()(JobCardContent);

